import { ExternalLink } from '@/ui/Link/ExternalLink';

import {
  StyledH1,
  StyledH2,
  StyledLink,
  StyledListItem,
  StyledParagraph,
  StyledSpan,
  StyledSubtitle,
  StyledUnorderedList,
} from './StyledComponents';

export const TermsService = () => (
  <div className="flex justify-center">
    <div className="py-[10.4rem] px-4 max-w-full tablet:max-w-[66.67%]">
      <StyledH1>Terms of Service</StyledH1>
      <StyledSubtitle>Last Updated: July 15, 2025</StyledSubtitle>

      <StyledParagraph>
        Welcome to Matcha (the &quot;App&quot;), a website application by ZeroEx, Inc. (together
        with all affiliates, &quot;ZeroEx,&quot; &quot;we,&quot; &quot;us&quot;). The App allows
        users to swap certain digital tokens through a variety of networks that involve the use of
        an underlying blockchain or other decentralized or permissioned infrastructure, including
        Ethereum, Binance Smart Chain,Polygon, Avalanche, Celo, Fantom, Optimism, Base and Arbitrum
        (the &quot;Multi-Chain Networks&quot;).
      </StyledParagraph>
      <StyledParagraph>
        This Terms of Service Agreement (the &quot;Agreement&quot;) includes the terms and
        conditions that govern your access to and use of the App. Please read the Agreement
        carefully. By accessing the App, you accept and agree to be bound by and to comply with the
        Agreement, including the mandatory arbitration provision in Section 16. If you do not agree
        to the terms of the Agreement, you must not access or use the App.
      </StyledParagraph>
      <StyledParagraph>
        <strong>ARBITRATION NOTICE AND CLASS ACTION WAIVER</strong>: EXCEPT FOR CERTAIN TYPES OF
        DISPUTES DESCRIBED IN SECTION 16 BELOW, YOU AGREE THAT DISPUTES BETWEEN YOU AND US WILL BE
        RESOLVED BY BINDING, INDIVIDUAL ARBITRATION AND YOU WAIVE YOUR RIGHT TO PARTICIPATE IN A
        CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
      </StyledParagraph>

      <StyledH2>1. ELIGIBILITY</StyledH2>
      <StyledParagraph>
        To be eligible to access and use the App, you must be at least 18 years old (or the age of
        majority where you reside, whichever is older), and must not be barred from using the App
        under applicable law. In addition, you must be able to form a legally binding contract
        online either on behalf of a company or as an individual.
      </StyledParagraph>
      <StyledParagraph>
        If you are entering the Agreement on behalf of a company or other legal entity, you
        represent that you have the legal authority to bind the company or other legal entity to the
        terms of the Agreement, can form a legally binding contract online, and have the full right,
        power and authority to enter into and to comply with the obligations of the Agreement.
      </StyledParagraph>
      <StyledParagraph>
        Additionally, by accessing the App, you represent and warrant that you are not subject to
        sanctions by the United States, and are not a citizen or resident of a state, country,
        territory or other jurisdiction that is embargoed by the United States. You further
        represent and warrant that your use of the App would not be illegal or otherwise violate any
        applicable domestic or foreign law, rule, statute, or regulation (&quot;Applicable
        Law&quot;).
      </StyledParagraph>
      <StyledParagraph>
        We may suspend, restrict or terminate your access to any or all of the features of the App,
        and/or block or bar any transactions of yours if: (a) We are so required by a subpoena,
        court order, or binding order of a government authority, or under any applicable laws and
        regulations; (b) You breach this Agreement including, without limitation by conducting any
        prohibited activities under this Agreement; (c) We determine to do so for any other reason
        in our sole discretion.
      </StyledParagraph>

      <StyledH2>2. MODIFICATIONS TO THESE TERMS</StyledH2>
      <StyledParagraph>
        We reserve the right, in our sole discretion, to modify the Agreement at any time. If we
        make changes, we will provide you with notice of such changes by sending an email, providing
        a notice through the App, or updating the date at the top of this Agreement. Unless we say
        otherwise in our notice or update, any modifications are effective immediately, and your
        continued use of the App will confirm your acceptance of the changes. If you do not agree to
        the amended Agreement, you must stop using the App.
      </StyledParagraph>

      <StyledH2>3. PROPRIETARY RIGHTS</StyledH2>
      <StyledParagraph>
        We do not claim any ownership rights in any content that users (including you) provide to be
        made available through the App (&quot;User Content&quot;). You agree that you own or have
        all rights, title, and interest, including all intellectual property rights, in any User
        Content you provide to us. You hereby grant us a worldwide, non-exclusive, royalty-free,
        sublicensable, and transferable license to use, copy, distribute, create derivative works
        of, display, and perform any User Content that you upload, submit, store, or send on or
        through the App.
      </StyledParagraph>
      <StyledParagraph>
        Subject to the foregoing, ZeroEx owns or is duly authorized to use all intellectual property
        and other rights in the App and its contents, including all text, images and trademarks
        displayed or provided on the App, and all App software. Unless expressly authorized by us,
        you may not copy, modify, adapt, rent, license, sell, publish, distribute, or otherwise
        permit any third party to access or use the App or any of its contents, nor may you
        decompile, reverse engineer or otherwise attempt to obtain the source code or underlying
        information or ideas of or related to the App or App Software. Provided that you are
        eligible, and in consideration for your compliance with the terms of this Agreement, you are
        hereby granted a single, personal, limited license to access and use the App. This license
        is non-exclusive, non-transferable, and freely revocable by us at any time without notice or
        cause. Use of the App or its contents for any purpose not expressly permitted by this
        Agreement is strictly prohibited.
      </StyledParagraph>
      <StyledParagraph>
        Unlike the App software, the underlying smart contract protocols operating on the
        Multi-Chain Networks that facilitate trades through the App are open source software and not
        ZeroEx proprietary software.
      </StyledParagraph>

      <StyledH2>4. DISCLOSURES; DISCLAIMERS; FEES</StyledH2>
      <StyledH2>4.1 WARRANTY DISCLAIMER</StyledH2>
      <StyledParagraph>
        TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW, THE APP (AND ANY OF ITS CONTENT OR
        FUNCTIONALITY) IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS, AND WE EXPRESSLY
        DISCLAIM, AND YOU HEREBY WAIVE, ANY REPRESENTATIONS, CONDITIONS OR WARRANTIES OF ANY KIND,
        WHETHER EXPRESS OR IMPLIED, LEGAL, STATUTORY OR OTHERWISE, OR ARISING FROM STATUTE,
        OTHERWISE IN LAW, COURSE OF DEALING, OR USAGE OF TRADE, INCLUDING, WITHOUT LIMITATION, THE
        IMPLIED OR LEGAL WARRANTIES AND CONDITIONS OF MERCHANTABILITY, MERCHANTABLE QUALITY, QUALITY
        OR FITNESS FOR A PARTICULAR PURPOSE, TITLE, SECURITY, AVAILABILITY, RELIABILITY, ACCURACY,
        QUIET ENJOYMENT AND NON-INFRINGEMENT OF THIRD PARTY RIGHTS. WITHOUT LIMITING THE FOREGOING,
        WE DO NOT REPRESENT OR WARRANT THAT THE APP (INCLUDING ANY RELATED DATA) WILL BE
        UNINTERRUPTED, AVAILABLE AT ANY PARTICULAR TIME OR ERROR-FREE. FURTHER, WE DO NOT WARRANT
        THAT ERRORS IN THE APP ARE CORRECTABLE OR WILL BE CORRECTED.
      </StyledParagraph>

      <StyledH2>4.2 DISCLAIMER ABOUT INFORMATION ACCURACY</StyledH2>
      <StyledParagraph>
        You are aware that we rely on third-party sources for information. Specifically, you are
        aware that we rely on third-party sources for information about certain digital tokens on
        the App, including without limitation token description, total supply, market cap and 24
        hour volume, and you are aware that we have the right to choose, change, and remove any
        third-party information source in our sole discretion. We are not responsible for the
        quality, accuracy, timeliness, completeness, or reliability of any of the digital token
        information via the App. You are obligated to collect sufficient information and keep
        yourself well informed before trading any digital tokens through the App.
      </StyledParagraph>

      <StyledH2>4.3 LIMIT ORDER AND FEES</StyledH2>
      <StyledParagraph>
        Limit Order allows you to buy or sell digital tokens at a specified price within a specified
        timeframe. ZeroEx makes NO GUARANTEE that a limit order will be matched or executed. You
        acknowledge and agree that we may charge fees on your trades of certain token pairs, if your
        limit orders of these trades are filled. You can view further information about fees we may
        charge at{' '}
        <ExternalLink href="https://help.matcha.xyz/en/articles/3953360-are-there-any-fees-to-make-a-trade">
          https://help.matcha.xyz/en/articles/3953360-are-there-any-fees-to-make-a-trade
        </ExternalLink>
        , which may be updated from time to time.
      </StyledParagraph>

      <StyledH2>4.4 LIQUIDITY SOURCE and RFQ ORDER</StyledH2>
      <StyledParagraph>
        You can trade digital tokens through RFQ orders with professional market makers who provide
        liquidity through the &quot;Request-for-Quote&quot; system (&quot;RFQ&quot; system) built
        into the 0x API. RFQ system is designed to enable professional market makers to provide
        competitive pricing. By default, the App will compare prices and network fees across various
        blockchain liquidity sources (including RFQ and AMMs) to find the optimal trading routes.
        Alternatively, you may manually choose the liquidity sources for your trades via the App.
        ZeroEx makes NO GUARANTEE that you’ll receive better pricing through RFQ orders than through
        other liquidity sources. You should be aware that your RFQ orders may not be matched or
        executed, and ZeroEx makes NO GUARANTEE in this regard.
      </StyledParagraph>

      <StyledH2>4.5 NETWORK FEES</StyledH2>
      <StyledParagraph>
        The App utilizes <StyledLink href="https://0x.org/">0x API</StyledLink> to connect users
        with the Multi-Chain Networks. You acknowledge and agree that transactions on the
        Multi-Chain Networks may require that you pay a fee, such as a &quot;gas fee&quot; on the
        Ethereum network, for the computational resources required to perform the transaction on the
        particular blockchain network (&quot;Network Fees&quot;).
      </StyledParagraph>
      <StyledParagraph>
        Where applicable, you are responsible for paying Network Fees imposed by the Multi-Chain
        Networks. You acknowledge and agree that ZeroEx has no discretion over any Multi-Chain
        Networks, or the payment of any Network Fees, if applicable, and we expressly disclaim all
        responsibility related thereto. Where applicable, you must ensure that you have a sufficient
        balance of the Multi-Chain Networks&rsquo; native tokens stored at your compatible wallet
        address (&quot;Wallet Address&quot;) to complete any transaction before initiating such
        transaction.
      </StyledParagraph>

      <StyledH2>4.6 MATCHA AUTO AND FEES</StyledH2>
      <StyledParagraph>
        Matcha Auto is a feature of the App that enables you to trade digital tokens on a particular
        blockchain network without holding any native tokens of that blockchain network (e.g., ETH
        for Ethereum). Instead of paying Network Fees, you will pay Settlement Fees, denominated in
        the type of tokens traded in your transaction, in an amount approximately equivalent to the
        Network Fees. You acknowledge and agree that there is no guarantee that Settlement Fees will
        be the same or less than the Network Fees you otherwise would have paid for a trade.
      </StyledParagraph>
      <StyledParagraph>
        Matcha Auto also charges you additional fees for trading certain token pairs. The additional
        fees will be included in the total Settlement Fees you will pay for authorizing a trade. You
        can view further information on these fees at
        <ExternalLink href="https://help.matcha.xyz/en/articles/3953360-are-there-any-fees-to-make-a-trade">
          https://help.matcha.xyz/en/articles/3953360-are-there-any-fees-to-make-a-trade
        </ExternalLink>
        , which may be updated from time to time.
      </StyledParagraph>
      <StyledParagraph>
        We reserve the right to charge other fees for your use of the App and adjust any fees at any
        time. If any fees are imposed, you will see these fees together with pricing of your
        transaction when you authorize the transaction.
      </StyledParagraph>

      <StyledH2>4.7 TRADE SURPLUS</StyledH2>
      <StyledParagraph>
        Matcha Auto and Matcha Standard trades will be subject to Trade Surplus, which represents
        the positive difference between a quoted trade and an executed trade, resulting from
        prevailing market conditions. The App will retain this Trade Surplus. Please note that your
        trades on the App will not be adversely impacted by Trade Surplus. You will receive no less
        than the quoted amount of a trade.
      </StyledParagraph>

      <StyledH2>4.8 OFA and OFA REBATE</StyledH2>
      <StyledParagraph>
        The App may use an Order Flow Auction (&quot;OFA&quot;) that enables some filled trades to
        be sent to private mempools where searchers bid for the right to bundle and settle the
        trades on Ethereum blockchain. For such OFA trades, we earn an OFA Rebate on certain token
        pairs. Neither OFA, or OFA Rebate we receive, will have any impact on the pricing, costs or
        terms of your trades.
      </StyledParagraph>

      <StyledH2>4.9 SWAP FEES ON CERTAIN NETWORKS</StyledH2>
      <StyledParagraph>
        Matcha Standard trades on certain blockchain networks are subject to swap fees. This fee is
        separate from any network fees (such as gas fees), or other fees we may charge. The swap fee
        will be clearly displayed to you before you authorize any transaction.
      </StyledParagraph>
      <StyledParagraph>
        We reserve the right to modify these fees or add fees to trades on other networks at any
        time. Any changes to fees will be disclosed and reflected in the transaction details shown
        to you before you authorize a transaction.
      </StyledParagraph>

      <StyledH2>4.10 CUSTODY AND CONTROL OF DIGITAL TOKENS</StyledH2>
      <StyledParagraph>
        You have full custody and control of the digital tokens in your third-party digital wallets
        at all times. We do not offer wallets, and never custody your digital tokens, or have access
        to, or retain the electronic private key of your digital wallet. As the owner and custodian
        of the digital tokens in your digital wallets, you shall bear all risk of loss of such
        digital tokens.
      </StyledParagraph>
      <StyledParagraph>
        You understand that ZeroEx is not registered or licensed by the Commodity Futures Trading
        Commission, Securities and Exchange Commission, Financial Crimes Enforcement Network, or any
        financial regulatory authority, and that no financial regulatory authority has reviewed or
        approved the App. You further understand that ZeroEx is not acting as an investment adviser
        or commodity trading adviser to any person, does not offer securities or derivatives
        services in the United States or to U.S. persons, and that the contents of the App do not
        constitute advice, endorsement, or recommendations concerning any commodity, security or
        other asset.
      </StyledParagraph>

      <StyledH2>4.11 RISKS ASSOCIATED WITH DIGITAL TOKENS</StyledH2>
      <StyledParagraph>
        By accessing and using the App, you represent that you understand the inherent risks
        associated with using cryptographic and blockchain-based systems, and that you have a
        working knowledge of the usage and intricacies of digital tokens such as bitcoin (BTC),
        ether (ETH), and other digital tokens such as those following the Ethereum Token Standard
        (ERC-20). ZeroEx does not control the underlying software protocols of any digital tokens
        accessible on the App. You agree that we are not responsible for the operation,
        functionality or security of the underlying protocols and not liable for any loss of token
        value you may encounter due to any operating change, malfunction or failure of the
        underlying protocols.
      </StyledParagraph>
      <StyledParagraph>
        You further understand that the markets for these digital tokens are highly volatile, and
        that there are risks associated with digital tokens including (but not limited to) those
        related to adoption, speculation, technology, security, and regulation. You acknowledge that
        the cost and speed of transacting with cryptographic and blockchain-based systems such as
        Ethereum are variable and may increase dramatically at any time. You understand and agree to
        assume full responsibility for all of the risks of accessing and using the App and
        interacting with the Ethereum Blockchain, and agree that ZeroEx is not responsible for any
        loss you may experience as a result of these risks.
      </StyledParagraph>
      <StyledParagraph>
        You should be aware that anyone can create digital tokens via the Multi-Chain Networks (such
        as ERC20 tokens on Ethereum). We make no representation about the nature, quality, or legal
        categorization of the token or associated project. You are responsible for doing your own
        research as well as ensuring that you may legally transact in this token in the jurisdiction
        where you reside.
      </StyledParagraph>

      <StyledH2>5. TAXES</StyledH2>
      <StyledParagraph>
        It is your sole responsibility to fulfill any tax obligations that may apply to transactions
        you conduct via the App. You should withhold, collect, report and remit the correct amounts
        of taxes to the appropriate tax authorities. We make reasonable efforts to make your
        transaction history available through your account, but we make no representation about the
        completeness or accuracy of that information.
      </StyledParagraph>

      <StyledH2>6. PRIVACY</StyledH2>
      <StyledParagraph>
        Please refer to our privacy policy, available at{' '}
        <StyledLink href="/privacy">matcha.xyz/privacy</StyledLink>, for information about how we
        collect, use, share and otherwise process information about you.
      </StyledParagraph>

      <StyledH2>7. CHANGES; SUSPENSION; TERMINATION</StyledH2>
      <StyledParagraph>
        We may, in our sole discretion, at any time and with or without prior notice to you, modify,
        suspend or disable, temporarily or permanently, the App, in whole or in part, for any reason
        whatsoever, including, but not limited to, as a result of a security incident.
      </StyledParagraph>
      <StyledParagraph>
        We will not be liable for any losses suffered by you resulting from any modification to the
        App or from any suspension or termination, for any reason, of your access to all or any
        portion of the App.
      </StyledParagraph>
      <StyledParagraph>
        Any provision which by law or by its nature should survive termination of this Agreement
        shall survive termination. By way of example, all of the following will survive termination:
        any obligation you have to indemnify us, any limitations on our liability, any terms
        regarding ownership or intellectual property rights, and terms regarding disputes with us,
        including without limitation the arbitration agreement.
      </StyledParagraph>

      <StyledH2>8. ELECTRONIC NOTICES</StyledH2>
      <StyledParagraph>
        You consent to receive all communications, agreements, documents, receipts, notices, and
        disclosures electronically (collectively, our &quot;Communications&quot;) that we provide in
        connection with the Agreement or the App. You agree that we may provide our Communications
        to you by posting them on the App. You may also contact our Legal team to request additional
        electronic copies of our Communications by sending a support request to legal@0x.org.
      </StyledParagraph>

      <StyledH2>9. INDEMNIFICATION</StyledH2>
      <StyledParagraph>
        You agree to hold harmless, release, defend, and indemnify us and our officers, directors,
        employees, contractors, agents, affiliates, and subsidiaries (&quot;Protected Parties&quot;)
        from and against all claims, damages, obligations, losses, liabilities, costs and expenses
        arising from: (a) your access to and use of the App; (b) your violation of any term or
        condition of this Agreement, the right of any third party, or any other applicable law,
        rule, or regulation; and (c) any other party&rsquo;s access and use of the App with your
        assistance or using any device or account that you own or control.
      </StyledParagraph>

      <StyledH2>10. PROHIBITED ACTIVITIES</StyledH2>
      <StyledParagraph>
        You agree not to engage in, or attempt to engage in, any of the following categories of
        prohibited activity in relation to your access to or use of the App:
      </StyledParagraph>
      <StyledUnorderedList>
        <StyledListItem>
          <StyledSpan>10.1 Intellectual Property Infringement</StyledSpan> - Activity that infringes
          or violates any person or entity&rsquo;s copyright, trademark, service mark, patent, right
          of publicity, right of privacy, or other proprietary or intellectual property rights under
          the law.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.2 Cyberattack</StyledSpan> - Activity that seeks to interfere with or
          compromise the integrity, security or proper functioning of any computer, server, network,
          personal device or other information technology system, including (but not limited to) the
          deployment of viruses and denial of service attacks.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.3 Fraud or Misrepresentation</StyledSpan> - Activity that seeks to defraud
          us or any other person or entity, including (but not limited to) providing any false,
          inaccurate, or misleading information in order to unlawfully obtain the property of
          another.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.4 Market Manipulation</StyledSpan> - Activity that violates any applicable
          law, rule, or regulation concerning the integrity of markets, including (but not limited
          to) the manipulative tactics commonly known as spoofing and wash trading.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.5 Gambling</StyledSpan> - Activity that stakes or risks something of value
          upon the outcome of a contest of others, an event, or a game of chance, including without
          limitation to lotteries, bidding fee auctions, political betting, sports forecasting and
          sweepstakes.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.6 IP Address Disguise:</StyledSpan> Activity that enables non-eligible
          persons to access or trade via the App by using any virtual private network, proxy
          service, or any other third party service, network, or product with the intent of
          disguising your IP address or location.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.7 Toxic RFQ Flow</StyledSpan> - Any trading activity, pattern, strategy or
          behavior that intentionally or systematically exploits latency, stale pricing,
          misquotations, information asymmetry, or other conditions that result in an unfair trading
          advantage or adverse selection against liquidity providers within any Request-for-Quote
          process.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.8 Any Other Unlawful Conduct</StyledSpan> - Activity that violates any
          applicable law, rule, or regulation of the United States or another relevant jurisdiction,
          including (but not limited to) the restrictions and regulatory requirements imposed by
          U.S. law.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>10.9 Any Other Inappropriate Conduct</StyledSpan> - Activity that exploits,
          misuses, alters, or manipulates the App or underlying technology for inappropriate
          purposes, or any other conduct determined to be inappropriate in our sole discretion.
        </StyledListItem>
      </StyledUnorderedList>

      <StyledH2>11. EXCLUSION OF CONSEQUENTIAL AND RELATED DAMAGES</StyledH2>
      <StyledParagraph>
        IN NO EVENT WILL ZEROEX BE LIABLE FOR ANY INCIDENTAL, INDIRECT, SPECIAL, PUNITIVE,
        EXEMPLARY, CONSEQUENTIAL OR SIMILAR DAMAGES OR LIABILITIES WHATSOEVER (INCLUDING, WITHOUT
        LIMITATION, DAMAGES FOR LOSS OF DATA, INFORMATION, REVENUE, GOODWILL, PROFITS OR OTHER
        BUSINESS OR FINANCIAL BENEFIT) ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE APP,
        WHETHER UNDER CONTRACT, TORT (INCLUDING NEGLIGENCE), CIVIL LIABILITY, STATUTE, STRICT
        LIABILITY, BREACH OF WARRANTIES, OR UNDER ANY OTHER THEORY OF LIABILITY, AND WHETHER OR NOT
        ZEROEX HAS BEEN ADVISED OF, KNEW OF OR SHOULD HAVE KNOWN OF THE POSSIBILITY OF SUCH DAMAGES.
      </StyledParagraph>

      <StyledH2>12. LIMITATION OF LIABILITY</StyledH2>
      <StyledParagraph>
        IN NO EVENT WILL ZEROEX’S AGGREGATE LIABILITY ARISING OUT OF OR IN CONNECTION WITH THE APP
        (AND ANY OF ITS CONTENT AND FUNCTIONALITY), ANY PERFORMANCE OR NON-PERFORMANCE OF ZEROEX,
        DIGITAL TOKENS, ZRX TOKENS OR ANY OTHER PRODUCT, SERVICE OR OTHER ITEM PROVIDED IN
        CONNECTION WITH THE APP, WHETHER UNDER CONTRACT, TORT (INCLUDING NEGLIGENCE), CIVIL
        LIABILITY, STATUTE, STRICT LIABILITY OR OTHER THEORY OF LIABILITY EXCEED THE AMOUNT OF FEES
        PAID BY YOU TO US IN THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT GIVING
        RISE TO THE CLAIM FOR LIABILITY.
      </StyledParagraph>

      <StyledH2>13. RELEASE</StyledH2>
      <StyledParagraph>
        TO THE EXTENT PERMITTED BY APPLICABLE LAW, IN CONSIDERATION FOR BEING ALLOWED TO USE THE
        APP, YOU HEREBY RELEASE AND FOREVER DISCHARGE ZEROEX FROM, AND HEREBY WAIVE AND RELINQUISH,
        EACH AND EVERY PAST, PRESENT AND FUTURE DISPUTE, CLAIM, CONTROVERSY, DEMAND, RIGHT,
        OBLIGATION, LIABILITY, ACTION AND CAUSE OF ACTION OF EVERY KIND AND NATURE (INCLUDING
        PERSONAL INJURIES, DEATH, AND PROPERTY DAMAGE), THAT HAS ARISEN OR ARISES DIRECTLY OR
        INDIRECTLY OUT OF, OR THAT RELATES DIRECTLY OR INDIRECTLY, TO THE APP.{' '}
        <span className="font-bold">
          YOU HEREBY WAIVE ANY APPLICABLE PROVISION IN LAW OR REGULATION IN CONNECTION WITH THE
          FOREGOING, INCLUDING THE PROVISIONS OF SECTION 1542 OF THE CALIFORNIA CIVIL CODE, WHICH
          STATES: &quot;A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS THAT THE CREDITOR OR RELEASING
          PARTY DOES NOT KNOW OR SUSPECT TO EXIST IN HIS OR HER FAVOR AT THE TIME OF EXECUTING THE
          RELEASE, AND THAT, IF KNOWN BY HIM OR HER WOULD HAVE MATERIALLY AFFECTED HIS OR HER
          SETTLEMENT WITH THE DEBTOR OR RELEASED PARTY.&quot;
        </span>
      </StyledParagraph>

      <StyledH2>14. ASSIGNMENT</StyledH2>
      <StyledParagraph>
        You may not assign any rights or licenses granted under the Agreement. We reserve the right
        to assign any rights and/or licenses under this Agreement without restriction, including but
        not limited to any ZeroEx affiliates or subsidiaries or any successors of ZeroEx’s business
        interests.
      </StyledParagraph>

      <StyledH2>15. FORCE MAJEURE</StyledH2>
      <StyledParagraph>
        We shall not be responsible for any delay or failure in performance of the App resulting
        directly or indirectly from any events or circumstances beyond our reasonable control,
        including but not limited to, natural disaster, civil unrest, terrorism, significant market
        volatility and failure of Internet services, equipment or software.
      </StyledParagraph>

      <StyledH2>16. DISPUTE RESOLUTION AND ARBITRATION</StyledH2>
      <StyledParagraph>
        Please read the following section carefully because it requires you to arbitrate certain
        disputes and claims with ZeroEx and limits the manner in which you can seek relief from us,
        unless you opt out of arbitration by following the instructions set forth below. In
        addition, arbitration precludes you from suing in court or having a jury trial. Both you and
        ZeroEx acknowledge and agree that for the purposes of any dispute arising out of or relating
        to the subject matter of this Agreement, ZeroEx’s officers, directors, employees and
        independent contractors (&quot;Personnel&quot;) are third-party beneficiaries of this
        Agreement, and that upon your acceptance of this Agreement, Personnel will have the right
        (and will be deemed to have accepted the right) to enforce this Agreement against you as the
        third-party beneficiary hereof.
      </StyledParagraph>
      <StyledParagraph>
        YOU AND ZEROEX AGREE THAT ALL CLAIMS AND DISPUTES ARISING OUT OF OR RELATED TO THIS
        AGREEMENT ARE PERSONAL TO YOU AND ZEROEX AND THAT ANY SUCH CLAIM OR DISPUTE WILL BE RESOLVED
        SOLELY THROUGH INDIVIDUAL ACTION, AND WILL NOT BE BROUGHT AS A CLASS ARBITRATION, CLASS
        ACTION OR ANY OTHER TYPE OF REPRESENTATIVE PROCEEDING. CLAIMS OF MORE THAN ONE CUSTOMER OR
        USER CANNOT BE ARBITRATED OR LITIGATED JOINTLY OR CONSOLIDATED WITH THOSE OF ANY OTHER
        CUSTOMER OR USER.
      </StyledParagraph>
      <StyledParagraph>
        Except for small claims disputes in which you or ZeroEx seeks to bring an individual action
        in small claims court located in the county or other applicable jurisdiction where you
        reside or disputes in which you or ZeroEx seeks injunctive or other equitable relief for the
        alleged unlawful use of intellectual property, YOU AND ZEROEX WAIVE ANY CONSTITUTIONAL AND
        STATUTORY RIGHTS TO A JURY TRIAL AND TO HAVE ANY DISPUTE ARISING OUT OF OR RELATED TO THIS
        AGREEMENT OR THE APP RESOLVED IN COURT. In any litigation between you and ZeroEx over
        whether to vacate or enforce an arbitration award, YOU AND ZEROEX WAIVE ALL RIGHTS TO A JURY
        TRIAL, and elect instead to have the dispute be resolved by a judge.
      </StyledParagraph>
      <StyledParagraph>
        Instead, for any dispute or claim that you have against ZeroEx or relating in any way to the
        Services, you agree to first contact ZeroEx and attempt to resolve the claim informally by
        sending a written notice of your claim (&quot;Notice&quot;) to ZeroEx by email at
        legal@0x.org. The Notice must include your name, residence address, email address, and
        telephone number, describe the nature and basis of the claim and set forth the specific
        relief sought. Our notice to you will be similar in form to that described above. If you and
        ZeroEx cannot reach an agreement to resolve the claim within thirty (30) days after such
        Notice is received, then either party may submit the dispute to binding arbitration
        administered by the JAMS, or, under the limited circumstances set forth above, in court. All
        disputes submitted to JAMS will be resolved through confidential, binding arbitration before
        one arbitrator. Arbitration proceedings will be held in San Francisco, California, in
        accordance with the JAMS Comprehensive Arbitration Rules &amp; Procedures (&quot;JAMS
        Rules&quot;). The most recent version of the JAMS Rules are available on the JAMS Site and
        are hereby incorporated by reference. You either acknowledge and agree that you have read
        and understand the JAMS Rules or waive your opportunity to read the JAMS Rules and waive any
        claim that the JAMS Rules are unfair or should not apply for any reason.
      </StyledParagraph>
      <StyledParagraph>
        You and ZeroEx agree that the enforceability of this Section 16 will be substantively and
        procedurally governed by the Federal Arbitration Act, 9 U.S.C. &sect; 1, et seq. (the
        &quot;FAA&quot;), to the maximum extent permitted by applicable law. As limited by the FAA,
        these Terms and the AAA Rules, the arbitrator will have exclusive authority to make all
        procedural and substantive decisions regarding any dispute and to grant any remedy that
        would otherwise be available in court, including the power to determine the question of
        arbitrability. The arbitrator may conduct only an individual arbitration and may not
        consolidate more than one individual&rsquo;s claims, preside over any type of class or
        representative proceeding or preside over any proceeding involving more than one individual.
      </StyledParagraph>
      <StyledParagraph>
        The arbitrator, ZeroEx, and you will maintain the confidentiality of any arbitration
        proceedings, judgments and awards, including, but not limited to, all information gathered,
        prepared and presented for purposes of the arbitration or related to the disputes. The
        arbitrator will have the authority to make appropriate rulings to safeguard confidentiality,
        unless the law provides to the contrary. The duty of confidentiality does not apply to the
        extent that disclosure is necessary to prepare for or conduct the arbitration hearing on the
        merits, in connection with a court application for a preliminary remedy or in connection
        with a judicial challenge to an arbitration award or its enforcement, or to the extent that
        disclosure is otherwise required by law or judicial decision.
      </StyledParagraph>
      <StyledParagraph>
        You and ZeroEx agree that for any arbitration you initiate, you will pay all AAA fees and
        costs. For any arbitration initiated by ZeroEx, ZeroEx will pay all AAA fees and costs.
      </StyledParagraph>
      <StyledParagraph>
        ANY CLAIM ARISING OUT OF OR RELATED TO THIS AGREEMENT MUST BE FILED WITHIN ONE YEAR AFTER
        SUCH CLAIM AROSE; OTHERWISE, THE CLAIM IS PERMANENTLY BARRED, WHICH MEANS THAT YOU AND
        ZEROEX WILL NOT HAVE THE RIGHT TO ASSERT THE CLAIM.
      </StyledParagraph>
      <StyledParagraph>
        You have the right to opt out of binding arbitration within 30 days of the date you first
        accepted the terms of this Section 16 by emailing us at{' '}
        <StyledLink href={'mailto:legal@0x.org'}>legal@0x.org</StyledLink>. In order to be
        effective, the opt-out notice must include your full name and address and clearly indicate
        your intent to opt out of binding arbitration. By opting out of binding arbitration, you are
        agreeing to resolve disputes in accordance with Section 17.
      </StyledParagraph>
      <StyledParagraph>
        If any portion of this Section 16 is found to be unenforceable or unlawful for any reason,
        the unenforceable or unlawful provision will be severed from this Agreement, severance of
        the unenforceable or unlawful provision will have no impact whatsoever on the remainder of
        this Section 16 or the parties&rsquo; ability to compel arbitration of any remaining claims
        on an individual basis under this Section 16, and to the extent that any claims must
        therefore proceed on a class, collective, consolidated, or representative basis, such claims
        must be litigated in a civil court of competent jurisdiction and not in arbitration, and the
        parties agree that litigation of those claims will be stayed pending the outcome of any
        individual claims in arbitration. Further, if any part of this Section 16 is found to
        prohibit an individual claim seeking public injunctive relief, that provision will have no
        effect to the extent such relief is allowed to be sought out of arbitration, and the
        remainder of this Section 16 will be enforceable.
      </StyledParagraph>

      <StyledH2>17. GOVERNING LAW</StyledH2>
      <StyledParagraph>
        The interpretation and enforcement of the Agreement, and any dispute related to the
        Agreement or the App, will be governed by and construed and enforced in accordance with the{' '}
        <StyledSpan>laws of California</StyledSpan>, as applicable, without regard to conflict of
        law rules or principles that would cause the application of the laws of any other
        jurisdiction. You agree that we may initiate a proceeding related to the enforcement or
        validity of our intellectual property rights in any court having jurisdiction. You agree
        that the U.S. District Court for the Northern District of California or a California state
        court located in San Francisco, California, is the proper forum for any appeals of an
        arbitration award, and that such courts will have personal jurisdiction over, and are the
        proper venue, for any court proceedings in the event that this Agreement&rsquo;s binding
        arbitration clause is found to be unenforceable.
      </StyledParagraph>

      <StyledH2>18. MISCELLANEOUS</StyledH2>
      <StyledParagraph>
        The failure of either you or us to exercise, in any way, any right herein shall not be
        deemed a waiver of any further rights hereunder. If any provision of this Agreement is found
        to be unenforceable or invalid, that provision will be limited or eliminated, to the minimum
        extent necessary, so that this Agreement shall otherwise remain in full force and effect and
        enforceable. You and ZeroEx agree that this Agreement is the complete and exclusive
        statement of the mutual understanding between you and ZeroEx, and that this Agreement
        supersedes and cancels all previous written and oral agreements, communications and other
        understandings relating to the subject matter of this Agreement. You hereby acknowledge and
        agree that you are not an employee, agent, partner, or joint venture of ZeroEx and you do
        not have any authority of any kind to bind ZeroEx in any respect whatsoever. Except as
        expressly set forth in the section above regarding the arbitration agreement, you and ZeroEx
        agree there are no third-party beneficiaries intended under this Agreement.
      </StyledParagraph>
    </div>
  </div>
);
